import { FAKE_PARTICPANT_JOINED, FAKE_PARTICPANT_LEFT } from '../mtApi/actionTypes';
import { FakeParticipant } from '../mtApi/constants';
import { ReducerRegistry, set } from '../redux';

import {
    CLEAR_RAISED_HAND_BY_PARTICIPANT,
    DOMINANT_SPEAKER_CHANGED,
    HIDE_RECORDING_CONSENT_MODAL,

    PARTICIPANT_DESIGNATION,
    PARTICIPANT_ID_CHANGED,
    PARTICIPANT_JOINED,
    PARTICIPANT_LEFT,
    PARTICIPANT_ORGANIZATION,
    PARTICIPANT_UPDATED,
    PARTICIPANT_VOLUME_CHANGED,
    PIN_PARTICIPANT,
    RAISE_HAND_UPDATED,
    RECORDING_STARTED_BY_USER,
    RESET_RECORDING_STARTED_BY_USER,
    SCREENSHARE_PARTICIPANT_NAME_CHANGED,
    SET_LOADABLE_AVATAR_URL,
    SET_MODERATOR_PIN,
    SET_PARTICIPANT_COLOR,
    SHOW_RECORDING_CONSENT_MODAL
} from './actionTypes';
import { LOCAL_PARTICIPANT_DEFAULT_ID, PARTICIPANT_ROLE } from './constants';

/**
 * Participant object.
 *
 * @typedef {Object} Participant
 * @property {string} id - Participant ID.
 * @property {string} name - Participant name.
 * @property {string} avatar - Path to participant avatar if any.
 * @property {string} role - Participant role.
 * @property {string} designation - Participant designation.
 * @property {string} organization - Participant organization.
 * @property {boolean} local - If true, participant is local.
 * @property {integer} volume - Participant audio volume.
 * @property {boolean} pinned - If true, participant is currently a
 * "PINNED_ENDPOINT".
 * @property {boolean} moderatorPin - If true, participant is a moderator.
 * @property {boolean} dominantSpeaker - If this participant is the dominant
 * speaker in the (associated) conference, {@code true}; otherwise,
 * {@code false}.
 * @property {string} color - Participant color.
 * @property {integer} volume - Participant audio volume.
 * speaker in the (associated) conference, {@code true}; otherwise,
 * {@code false}.
 * @property {string} email - Participant email.
 * @property {boolean} isShowConsentModal - If true, recording consent modal
 * should be shown to this participant.
 * @property {boolean} isRecordingStartedByUser - If true, recording has been started by this participant.
 */

/**
 * The participant properties which cannot be updated through
 * {@link PARTICIPANT_UPDATED}. They either identify the participant or can only
 * be modified through property-dedicated actions.
 *
 * @type {string[]}
 */
const PARTICIPANT_PROPS_TO_OMIT_WHEN_UPDATE = [

    // The following properties identify the participant:
    'conference',
    'id',
    'local',
    'volume',


    // The following properties can only be modified through property-dedicated
    // actions:
    'dominantSpeaker',
    'pinned',
    'color',
    'designation',
    'organization',
    'moderatorPin'
];

/**
 * Listen for actions which add, remove, or update the set of participants in
 * the conference.
 *
 * @param {Participant[]} state - List of participants to be modified.
 * @param {Object} action - Action object.
 * @param {string} action.type - Type of action.
 * @param {Participant} action.participant - Information about participant to be
 * added/removed/modified.
 * @returns {Participant[]}
 */
ReducerRegistry.register('features/base/participants', (state = [], action) => {
    switch (action.type) {
    case SET_LOADABLE_AVATAR_URL:
    case DOMINANT_SPEAKER_CHANGED:
    case PARTICIPANT_ID_CHANGED:
    case PARTICIPANT_UPDATED:
    case SET_PARTICIPANT_COLOR:
    case CLEAR_RAISED_HAND_BY_PARTICIPANT:
    case RAISE_HAND_UPDATED:
    case PARTICIPANT_DESIGNATION:
    case PARTICIPANT_ORGANIZATION:
    case SHOW_RECORDING_CONSENT_MODAL:
    case HIDE_RECORDING_CONSENT_MODAL:
    case RECORDING_STARTED_BY_USER:
    case RESET_RECORDING_STARTED_BY_USER:
        return state.map(p => _participant(p, action));
    case SET_MODERATOR_PIN:
        // Pass all participants to handle exclusive moderator pin (only one at a time)
        return state.map(p => _participant(p, {
            ...action,
            allParticipants: state
        }));
    case PIN_PARTICIPANT:
        // Pass all participants to _participant for moderator pin logic
        return state.map(p => _participant(p, {
            ...action,
            allParticipants: state
        }));

    case SCREENSHARE_PARTICIPANT_NAME_CHANGED: {
        const { id, name } = action;

        if (state.sortedRemoteVirtualScreenshareParticipants.has(id)) {
            state.sortedRemoteVirtualScreenshareParticipants.delete(id);

            const sortedRemoteVirtualScreenshareParticipants = [
                ...state.sortedRemoteVirtualScreenshareParticipants
            ];

            sortedRemoteVirtualScreenshareParticipants.push([ id, name ]);
            sortedRemoteVirtualScreenshareParticipants.sort((a, b) =>
                a[1].localeCompare(b[1])
            );

            state.sortedRemoteVirtualScreenshareParticipants = new Map(
                    sortedRemoteVirtualScreenshareParticipants
            );
        }

        return { ...state };
    }
    case PARTICIPANT_JOINED:
    {
        const joinedParticipant = _participantJoined(action);
        const existingIndex = state.findIndex(p => p.id === joinedParticipant.id
            && p.conference === joinedParticipant.conference);

        if (existingIndex !== -1) {
            const nextState = [ ...state ];

            nextState[existingIndex] = {
                ...nextState[existingIndex],
                ...joinedParticipant
            };

            return nextState;
        }

        return [ ...state, joinedParticipant ];
    }

    case FAKE_PARTICPANT_JOINED: {
        // Handle fake screenshare participants
        const { id, name, fakeParticipant, conference } = action;

        const fakeParticipantData = {
            id,
            name,
            conference,
            fakeParticipant,
            isFakeParticipant: fakeParticipant,
            local: fakeParticipant === FakeParticipant.LocalScreenShare,
            role: PARTICIPANT_ROLE.NONE
        };
        const existingIndex = state.findIndex(p => p.id === id
            && p.conference === conference
            && p.isFakeParticipant);

        if (existingIndex !== -1) {
            const nextState = [ ...state ];

            nextState[existingIndex] = {
                ...nextState[existingIndex],
                ...fakeParticipantData
            };

            return nextState;
        }

        return [ ...state, fakeParticipantData ];
    }

    case PARTICIPANT_VOLUME_CHANGED: {
        const { id, volume } = action;

        return state.map(p => {
            if (p.id === id) {
                return {
                    ...p,
                    volume
                };
            }

            return p;
        });
    }

    case PARTICIPANT_LEFT: {
        // XXX A remote participant is uniquely identified by their id in a
        // specific MHConference instance. The local participant is uniquely
        // identified by the very fact that there is only one local participant
        // (and the fact that the local participant "joins" at the beginning of
        // the app and "leaves" at the end of the app).
        const { conference, id } = action.participant;

        return state.filter(
                p =>
                    !(
                        p.id === id

                        // XXX Do not allow collisions in the IDs of the local
                        // participant and a remote participant cause the removal of
                        // the local participant when the remote participant's
                        // removal is requested.
                        && p.conference === conference
                        && (conference || p.local)
                    )
        );
    }

    case FAKE_PARTICPANT_LEFT: {
        // Handle fake screenshare participant removal
        const { id, conference } = action;

        return state.filter(p => !(p.id === id && p.conference === conference && p.isFakeParticipant));
    }
    }

    return state;
});

/**
 * Reducer function for a single participant.
 *
 * @param {Participant|undefined} state - Participant to be modified.
 * @param {Object} action - Action object.
 * @param {string} action.type - Type of action.
 * @param {Participant} action.participant - Information about participant to be
 * added/modified.
 * @param {MHConference} action.conference - Conference instance.
 * @private { String }
 * @returns {Participant}
 */
function _participant(state = {}, action) {
    switch (action.type) {
    case DOMINANT_SPEAKER_CHANGED:
        // Only one dominant speaker is allowed.
        return set(state, 'dominantSpeaker', state.id === action.participant.id);

    case PARTICIPANT_ID_CHANGED: {
        // A participant is identified by an id-conference pair. Only the local
        // participant is with an undefined conference.
        const { conference } = action;

        if (
            state.id === action.oldValue
                && state.conference === conference
                && (conference || state.local)
        ) {
            return {
                ...state,
                id: action.newValue
            };
        }
        break;
    }

    case SET_PARTICIPANT_COLOR:
        if (state.id === action.id) {
            return {
                ...state,
                color: action.color
            };
        }
        break;
    case SET_LOADABLE_AVATAR_URL:
    case PARTICIPANT_UPDATED: {
        const { participant } = action;
        let { id } = participant;
        const { local } = participant;

        if (!id && local) {
            id = LOCAL_PARTICIPANT_DEFAULT_ID;
        }

        if (state.id === id) {
            const newState = { ...state };

            for (const key in participant) {
                if (
                    participant.hasOwnProperty(key)
                        && PARTICIPANT_PROPS_TO_OMIT_WHEN_UPDATE.indexOf(key) === -1
                ) {
                    newState[key] = participant[key];
                }
            }

            return newState;
        }
        break;
    }

    case RAISE_HAND_UPDATED:
        // raiseHandUpdated is a special case of participant update
        if (state.id === action.id) {
            return {
                ...state,
                raiseHand: !action.participant.raiseHand || false
            };
        }
        break;

    case PARTICIPANT_DESIGNATION:
        if (state.id === action.id) {
            return {
                ...state,
                designation: action.designation
            };
        }
        break;

    case PARTICIPANT_ORGANIZATION:
        if (state.id === action.id) {
            return {
                ...state,
                organization: action.organization
            };
        }
        break;

    case CLEAR_RAISED_HAND_BY_PARTICIPANT:
        // Clear the raise hand for a specific participant.
        if (state.id === action.id) {
            return {
                ...state,
                raiseHand: !action.participant.raiseHand || false
            };
        }
        break;

        // In your participant reducer

    case PIN_PARTICIPANT: {
        // If any participant has moderatorPin true, no one can pin anyone else
        // if (action.allParticipants && action.allParticipants.some(p => p.moderatorPin)) {
        //     // If this participant is not the moderator, ensure pinned is false
        //     return {
        //         ...state,
        //         pinned: false
        //     };
        // }

        const { id, isPinned } = action.participant;

        if (typeof isPinned === 'boolean') {
            // If isPinned is passed, set pinned state to that value
            if (state.id === id && isPinned) {
                return {
                    ...state,
                    pinned: isPinned
                };
            }

        }
        if (state.id === id) {
            // Toggle pin state for the target participant
            const newPinnedState = !state.pinned;

            return {
                ...state,
                pinned: newPinnedState,

                // Keep moderator pin unchanged if it exists
                moderatorPin: state.moderatorPin || false
            };
        }

        // For other participants, unpin them (only one participant can be pinned at a time)
        return {
            ...state,
            pinned: false
        };
    }

    case SET_MODERATOR_PIN: {
        const { id, action: pinAction } = action.participant;

        if (state.id === id) {
            const shouldPin = pinAction === 'pin';

            return {
                ...state,
                moderatorPin: shouldPin,
                pinned: shouldPin // Moderator pin also sets regular pin
            };
        }

        // For other participants, remove moderator pin if a new one is being pinned
        if (pinAction === 'pin') {
            // When a new participant is moderator pinned, remove moderator pin from others
            return {
                ...state,
                moderatorPin: false,
                pinned: false
            };
        }

        // Remove moderator pin if unpinning everyone
        if (pinAction === 'unpin_all') {
            return {
                ...state,
                moderatorPin: false,
                pinned: false
            };
        }

        return state;
    }

    case SHOW_RECORDING_CONSENT_MODAL:
        if (state.id === action.participant.id) {
            return set(state, 'isShowConsentModal', true);
        }
        break;

    case HIDE_RECORDING_CONSENT_MODAL:
        if (state.id === action.participant.id) {
            return set(state, 'isShowConsentModal', false);
        }
        break;

    case RECORDING_STARTED_BY_USER:
        // This participant has started the recording.

        if (state.id === action.participant.id) {
            return set(state, 'isRecordingStartedByUser', true);
        }
        break;

    case RESET_RECORDING_STARTED_BY_USER:
        // Reset the flag so the consent dialog fires fresh on the next recording session.
        if (state.id === action.participant.id) {
            return {
                ...state,
                isRecordingStartedByUser: false,
                isShowConsentModal: false
            };
        }
        break;
    }

    return state;
}

/**
 * Reduces a specific redux action of type {@link PARTICIPANT_JOINED} in the
 * feature base/participants.
 *
 * @param {Action} action - The redux action of type {@code PARTICIPANT_JOINED}
 * to reduce.
 * @private
 * @returns {Object} The new participant derived from the payload of the
 * specified {@code action} to be added into the redux state of the feature
 * base/participants after the reduction of the specified
 * {@code action}.
 */
function _participantJoined({ participant }) {
    const {
        avatarURL,
        botType,
        connectionStatus,
        dominantSpeaker,
        email,
        color,
        fakeParticipant,
        isFakeParticipant,
        isJigasi,
        loadableAvatarUrl,
        local,
        organization,
        designation,
        name,
        pinned,
        moderatorPin,
        presence,
        role,
        ownerId
    } = participant;
    let { conference, id } = participant;

    if (local) {
        // conference
        //
        // XXX The local participant is not identified in association with a
        // MHConference because it is identified by the very fact that it is
        // the local participant.
        conference = undefined;

        // id
        id || (id = LOCAL_PARTICIPANT_DEFAULT_ID);
    }

    return {
        avatarURL,
        botType,
        conference,
        connectionStatus,
        dominantSpeaker: dominantSpeaker || false,
        email,
        id,
        color,
        isFakeParticipant,
        isJigasi,
        fakeParticipant,
        isShowConsentModal: false,
        isRecordingStartedByUser: false,
        loadableAvatarUrl,
        volume: 100,
        local: local || false,
        name,
        moderatorPin: moderatorPin || false,
        designation,
        organization,
        ownerId,
        pinned: pinned || false,
        presence,
        role: role || PARTICIPANT_ROLE.NONE
    };
}
